/**
 * Mitt. Bokningar och tilldelningar för den roll som inte har det stora bokningskortet.
 * Tilldelningar öppnar samma lista som tidigare. Bokningar öppnar kommande bokningar.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '@/services/api/client';
import { useLanguage, useTranslation } from '@/store/languageStore';
import { bookingsOnLocalDay, upcomingBookings } from '@/utils/myBookingsWidget';
import MyAssignmentsWidget from './MyAssignmentsWidget';
import MyBookingsList, { useMyBookingsData } from './MyBookingsList';

type MinePanel = 'bookings' | 'assignments';

function uiLocale(language: string): string {
  if (language === 'en') return 'en-GB';
  if (language === 'lt') return 'lt-LT';
  if (language === 'de') return 'de-DE';
  return 'sv-SE';
}

function MineDialog({ panel, onClose }: { panel: MinePanel; onClose: () => void }) {
  const { t } = useTranslation();

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 sm:p-8" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={panel === 'bookings' ? t('bookingWidget.title') : t('assignments.myAssignments')}
        className={`relative mt-8 w-full rounded-xl border border-borderDefault bg-bgCard p-4 shadow-xl ${panel === 'bookings' ? 'max-w-5xl' : 'max-w-3xl'}`}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 rounded-lg p-1 text-textTertiary hover:bg-bgHover"
          aria-label={t('common.close')}
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
        <div className="pr-8">
          {panel === 'bookings' ? <MyBookingsList listClassName="max-h-[70vh] overflow-y-auto" /> : <MyAssignmentsWidget startExpanded />}
        </div>
      </div>
    </div>,
    document.body,
  );
}

const MineWidget: React.FC = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const locale = uiLocale(language);
  const [panel, setPanel] = useState<MinePanel | null>(null);
  const bookings = useMyBookingsData();
  const assignments = useQuery(
    ['mine-assignments'],
    async () => {
      const response = await api.get('/api/dashboard/data/my-assignments');
      if (!response.data.success) throw new Error('Failed to load assignments');
      return (response.data.data || []) as unknown[];
    },
    { staleTime: 60 * 1000 }
  );

  const now = useMemo(() => new Date(), []);
  const upcoming = useMemo(() => upcomingBookings(bookings.data?.items ?? [], now), [bookings.data, now]);
  const todayCount = bookingsOnLocalDay(upcoming, now).length;
  const bookingWaiting = bookings.isLoading && !bookings.data;
  const assignmentWaiting = assignments.isLoading && !assignments.data;
  const bookingCount = bookingWaiting || bookings.isError ? null : upcoming.length;
  const assignmentCount = assignmentWaiting || assignments.isError ? null : (assignments.data?.length ?? 0);
  const countText = (value: number | null) => (value == null ? '–' : value.toLocaleString(locale));

  return (
    <div className="flex h-full min-h-0 flex-col">
      <h2 className="text-base font-semibold text-textDefault">{t('mine.title')}</h2>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => setPanel('bookings')}
          disabled={bookings.isError}
          className="rounded-xl bg-bgBase px-3 py-3 text-left hover:bg-bgHover disabled:cursor-default"
        >
          <div className="text-3xl font-semibold tabular-nums text-textDefault">{countText(bookingCount)}</div>
          <div className="text-sm text-textSecondary">{t('mine.bookings')}</div>
        </button>
        <button
          type="button"
          onClick={() => setPanel('assignments')}
          disabled={assignments.isError}
          className="rounded-xl bg-bgBase px-3 py-3 text-left hover:bg-bgHover disabled:cursor-default"
        >
          <div className="text-3xl font-semibold tabular-nums text-textDefault">{countText(assignmentCount)}</div>
          <div className="text-sm text-textSecondary">{t('mine.assignments')}</div>
        </button>
      </div>
      {!bookings.isError && !bookingWaiting && (
        <p className="mt-4 text-sm text-textSecondary">
          {todayCount === 0
            ? t('mine.nothingToday')
            : t(todayCount === 1 ? 'mine.bookedToday' : 'mine.bookedTodayMany').replace('{count}', String(todayCount))}
        </p>
      )}
      <button
        type="button"
        onClick={() => navigate('/booking/calendar?newBooking=1')}
        className="mt-4 flex w-full items-center justify-center gap-1 rounded-xl border border-teal-500/40 py-3 text-sm font-medium text-teal-400 hover:bg-bgHover"
      >
        <PlusIcon className="h-4 w-4" />
        {t('bookingWidget.newBooking')}
      </button>
      {panel && <MineDialog panel={panel} onClose={() => setPanel(null)} />}
    </div>
  );
};

export default MineWidget;
